import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Collapse,
  Divider,
  Grid,
  Group,
  Image,
  List,
  Loader,
  Modal,
  Paper,
  Skeleton,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

import { AppShell } from '../../components/app-shell/AppShell';
import { AvailabilityCalendar } from '../../components/calendar/AvailabilityCalendar';
import { MarketingShell } from '../../components/marketing/MarketingShell';
import {
  MilestonesBlock,
  type VenueMilestones,
} from '../../components/profile/MilestonesBlock';
import { ReportButton } from '../../components/ReportButton';
import { type ReviewCardData } from '../../components/reviews/ReviewCard';
import { ReviewsSection } from '../../components/reviews/ReviewsSection';
import { PageFrame } from '../../components/ui/PageFrame';
import { SectionTitle } from '../../components/ui/SectionTitle';
import { fetchOk } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { formatDateRangeMs } from '../../lib/date-format';
import { track } from '../../lib/telemetry';

// ─── Types (mirror worker DTOs) ─────────────────────────────────────────────

type AggregateBucket = { avg: number; count: number };
type AggregateScores = {
  communication?: AggregateBucket;
  hospitality?: AggregateBucket;
  performance?: AggregateBucket;
  safety?: AggregateBucket;
  exchangeRespected?: AggregateBucket;
};

type VenueDetailDTO = {
  id: string;
  slug: string;
  name: string;
  summary: string | null;
  description: string;
  city: string;
  region: string | null;
  country: string;
  postcode: string | null;
  addressLine: string | null;
  lat: number;
  lng: number;
  exactLocation: boolean;
  capacityMin: number;
  capacityMax: number;
  timezone: string;
  usualEventTimes: string | null;
  houseRules: string | null;
  stipendOffered: boolean;
  stipendDescription: string | null;
  travelContributionDefault?: boolean;
  travelContributionDescription?: string | null;
  equipmentDefault?: boolean;
  equipmentDescription?: string | null;
  promotionDefault?: boolean;
  promotionDescription?: string | null;
  recordingDefault?: boolean;
  recordingDescription?: string | null;
  verified: boolean;
  foundingMember: boolean;
  /** Wave 5: account-level founding flag — host is a founding host even if
   *  this particular venue is not the founding-cohort venue. */
  foundingHostAccount: boolean;
  artForms: { slug: string; name: string; group: string }[];
  venueTypes: { slug: string; name: string }[];
  vibeTags: { slug: string; name: string }[];
  amenities: { slug: string; name: string; scope: string }[];
  images: {
    id: string;
    url: string;
    altText: string;
    caption: string | null;
    isHero: boolean;
    position: number;
    widthPx: number;
    heightPx: number;
  }[];
  host: {
    displayName: string;
    completedExchanges: number;
    aggregateReviewScores: AggregateScores | null;
  };
  reviewSummary: {
    visible: boolean;
    completedExchanges: number;
    threshold: number;
    aggregateReviewScores: AggregateScores | null;
    reviewCount: number;
  };
  availability: {
    blocks: { startDate: string; endDate: string }[];
    openWindows: { startDate: string; endDate: string }[];
    defaultState: 'available' | 'unavailable';
  } | null;
  publishedAt: number | null;
  /** T4 — released reviews + per-review response surface. */
  reviews: ReviewCardData[];
  reviews_total: number;
  /** T4 — host user id, used to decide if the viewer is the subject. */
  reviewee_user_id: string;
};

async function fetchVenue(slug: string): Promise<VenueDetailDTO | null> {
  const res = await fetch(`/api/v/venues/${encodeURIComponent(slug)}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error('Couldn\'t load this venue.');
  return (await res.json()) as VenueDetailDTO;
}

/**
 * Wave A — milestones fetch is independent of the main detail fetch so a
 * milestones failure doesn't block the page. Returns null on any error.
 */
async function fetchVenueMilestones(slug: string): Promise<VenueMilestones | null> {
  try {
    const res = await fetchOk(`/api/v/venues/${encodeURIComponent(slug)}/milestones`);
    return (await res.json()) as VenueMilestones;
  } catch {
    return null;
  }
}

export const Route = createFileRoute('/venues/$slug')({
  component: VenueDetailPage,
});

// FR-144: stable, module-scope full-width AppShell wrapper (see the twin note
// on the artist detail page) so the anonymous marketing branch and the
// signed-in branch both defer width to the inner PageFrame.
function AppShellFull({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <AppShell tone="full">{children}</AppShell>;
}

function VenueDetailPage(): React.JSX.Element {
  const { slug } = Route.useParams();
  const { user } = useAuth();
  const query = useQuery({
    queryKey: ['venue', slug],
    queryFn: () => fetchVenue(slug),
  });
  const milestonesQuery = useQuery({
    queryKey: ['venue', slug, 'milestones'],
    queryFn: () => fetchVenueMilestones(slug),
  });

  let body: React.JSX.Element;
  if (query.isLoading) {
    body = (
      <Stack gap="md">
        <Skeleton h={320} />
        <Skeleton h={32} w="50%" />
        <Skeleton h={16} w="30%" />
        <Skeleton h={120} />
      </Stack>
    );
  } else if (query.isError) {
    body = (
      <Paper p="xl" withBorder>
        <Stack gap="xs" align="center">
          <Title order={3}>This venue couldn&apos;t load right now.</Title>
          <Text c="dimmed">Refresh and try again.</Text>
          <Button variant="default" onClick={() => void query.refetch()}>
            Try again
          </Button>
        </Stack>
      </Paper>
    );
  } else if (!query.data) {
    body = <NotFound slug={slug} />;
  } else {
    body = (
      <VenueDetail
        venue={query.data}
        milestones={milestonesQuery.data ?? null}
      />
    );
  }

  // FR-144: anonymous visitors get marketing chrome + sign-up CTAs instead of
  // the authenticated productivity shell (whose nav dead-ends at sign-in).
  const Shell = user === null ? MarketingShell : AppShellFull;
  return (
    <Shell>
      <PageFrame tone="content">{body}</PageFrame>
    </Shell>
  );
}

function VenueDetail({
  venue,
  milestones,
}: {
  venue: VenueDetailDTO;
  milestones: VenueMilestones | null;
}): React.JSX.Element {
  const { user } = useAuth();
  const place = [venue.city, venue.region, venue.country].filter(Boolean).join(', ');
  const hero = venue.images.find((i) => i.isHero) ?? venue.images[0];

  // Fire venue.detail_viewed once per venue id. The query that loaded `venue`
  // is in the parent; here we know the load succeeded.
  useEffect(() => {
    track('venue.detail_viewed', {
      isFounding: venue.foundingMember,
      foundingHost: venue.foundingHostAccount,
      verified: venue.verified,
      hasStipend: venue.stipendOffered,
    });
  }, [venue.id, venue.foundingMember, venue.foundingHostAccount, venue.verified, venue.stipendOffered]);

  // Group amenities by scope.
  const amenitiesByScope = useMemo(() => {
    const map = new Map<string, { slug: string; name: string }[]>();
    for (const a of venue.amenities) {
      const list = map.get(a.scope) ?? [];
      list.push({ slug: a.slug, name: a.name });
      map.set(a.scope, list);
    }
    return map;
  }, [venue.amenities]);

  // Lightbox state.
  const [lightboxOpen, lightboxCtl] = useDisclosure(false);
  const [lightboxIdx, setLightboxIdx] = useState(0);

  const openLightbox = (i: number): void => {
    setLightboxIdx(i);
    lightboxCtl.open();
  };

  return (
    <>
      <Stack gap="xl">
        <Anchor component={Link} to="/browse" c="dimmed" size="sm">
          ← Browse venues
        </Anchor>

        {/* Hero + gallery thumbs */}
        <Stack gap="sm">
          {hero ? (
            <Box
              onClick={() => openLightbox(venue.images.indexOf(hero))}
              style={{ cursor: 'pointer' }}
            >
              <Image
                src={hero.url}
                alt={hero.altText}
                h={{ base: 220, sm: 360, lg: 440 }}
                fit="cover"
                radius="md"
              />
            </Box>
          ) : (
            <Center
              h={{ base: 180, sm: 280, lg: 340 }}
              style={{
                borderRadius: 'var(--mantine-radius-md)',
                background: 'var(--wwaave-accent-gradient)',
              }}
            >
              <Text
                fz={{ base: 48, sm: 64, lg: 80 }}
                fw={700}
                style={{
                  opacity: 0.18,
                  userSelect: 'none',
                  letterSpacing: 4,
                  color: 'light-dark(var(--mantine-color-brand-9), var(--mantine-color-brand-1))',
                }}
              >
                {venue.name
                  .split(/\s+/)
                  .slice(0, 3)
                  .map((w) => w[0]?.toUpperCase() ?? '')
                  .join('')}
              </Text>
            </Center>
          )}
          {venue.images.length > 1 && (
            <Group gap="xs" wrap="nowrap" style={{ overflowX: 'auto' }}>
              {venue.images.map((img, i) => (
                <Box
                  key={img.id}
                  onClick={() => openLightbox(i)}
                  style={{ cursor: 'pointer', flexShrink: 0 }}
                >
                  <Image src={img.url} alt={img.altText} w={96} h={72} fit="cover" radius="sm" />
                </Box>
              ))}
            </Group>
          )}
        </Stack>

        {/* Title row */}
        <Stack gap="xs">
          <Group justify="space-between" wrap="wrap" gap="sm" align="flex-start">
            <Title order={1}>{venue.name}</Title>
            <ReportButton
              subjectType="venue"
              subjectId={venue.id}
              subjectLabel={venue.name}
              variant="button"
            />
          </Group>
          <Group gap="xs" wrap="wrap">
            <Text c="dimmed">{place}</Text>
            {venue.verified && <Badge variant="light" color="tide">Verified</Badge>}
            {venue.foundingMember && <Badge variant="light" color="sun">Founding venue</Badge>}
            {venue.foundingHostAccount && (
              <Badge variant="light" color="sun">Founding host</Badge>
            )}
          </Group>
          {venue.reviewSummary.visible && venue.reviewSummary.aggregateReviewScores && (
            <Text size="sm" c="dimmed">
              ★ {weightedAverage(venue.reviewSummary.aggregateReviewScores).toFixed(1)} (
              {venue.reviewSummary.reviewCount} review{venue.reviewSummary.reviewCount === 1 ? '' : 's'})
            </Text>
          )}
        </Stack>

        <MilestonesBlock kind="venue" data={milestones} />

        {/* Wave B: open weeks the host has posted, visible to signed-in
            artists. Anonymous + non-artist viewers get a 401/403 from the
            endpoint, which becomes a no-op render. */}
        <OpenWeeksSection slug={venue.slug} />

        {/* Two-column body */}
        <Grid gap="lg">
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Stack gap="lg">
              <section aria-labelledby="about-heading">
                <Box id="about-heading" mb="xs">
                  <SectionTitle title="About" order={3} />
                </Box>
                <Text style={{ whiteSpace: 'pre-wrap' }} maw={680}>{venue.description}</Text>
              </section>

              {venue.vibeTags.length > 0 && (
                <section aria-labelledby="vibe-heading">
                  <Title order={4} id="vibe-heading" mb="xs">
                    Vibe
                  </Title>
                  <Group gap="xs" wrap="wrap">
                    {venue.vibeTags.map((t) => (
                      <Badge key={t.slug} variant="light">
                        {t.name}
                      </Badge>
                    ))}
                  </Group>
                </section>
              )}

              <section aria-labelledby="capacity-heading">
                <Title order={4} id="capacity-heading" mb="xs">
                  Capacity
                </Title>
                <Text>
                  {venue.capacityMin}–{venue.capacityMax} people
                </Text>
              </section>

              {venue.artForms.length > 0 && (
                <section aria-labelledby="artforms-heading">
                  <Title order={4} id="artforms-heading" mb="xs">
                    Art forms welcome
                  </Title>
                  <Group gap="xs" wrap="wrap">
                    {venue.artForms.map((a) => (
                      <Badge key={a.slug} variant="outline">
                        {a.name}
                      </Badge>
                    ))}
                  </Group>
                </section>
              )}

              {venue.amenities.length > 0 && (
                <section aria-labelledby="offered-heading">
                  <Title order={4} id="offered-heading" mb="xs">
                    What&apos;s offered
                  </Title>
                  <Stack gap="xs">
                    {AMENITY_SCOPE_ORDER.map((scope) => {
                      const list = amenitiesByScope.get(scope);
                      if (!list || list.length === 0) return null;
                      return (
                        <Text key={scope} size="sm">
                          <Text component="span" fw={500}>
                            {scopeLabel(scope)}:
                          </Text>{' '}
                          {list.map((a) => a.name).join(', ')}
                        </Text>
                      );
                    })}
                  </Stack>
                </section>
              )}

              <ExtrasSection venue={venue} />

              {venue.houseRules && (
                <section aria-labelledby="rules-heading">
                  <Title order={4} id="rules-heading" mb="xs">
                    House rules
                  </Title>
                  <Text style={{ whiteSpace: 'pre-wrap' }} maw={680}>{venue.houseRules}</Text>
                </section>
              )}
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 4 }}>
            <Stack gap="md" pos={{ base: 'static', md: 'sticky' }} top={16}>
              <Card withBorder padding="md">
                <Stack gap="xs">
                  <Text size="sm" c="dimmed">
                    Hosted by
                  </Text>
                  <Title order={4}>{venue.host.displayName}</Title>
                  <Text size="sm" c="dimmed">
                    {venue.host.completedExchanges} exchange
                    {venue.host.completedExchanges === 1 ? '' : 's'} hosted
                  </Text>
                  {venue.verified && (
                    <Badge size="sm" variant="light" color="tide" w="fit-content">
                      Verified host
                    </Badge>
                  )}
                  {venue.foundingHostAccount && (
                    <Badge size="sm" variant="light" color="sun" w="fit-content">
                      Founding host
                    </Badge>
                  )}
                </Stack>
              </Card>

              {venue.stipendOffered && (
                <Card
                  withBorder
                  padding="md"
                  style={{
                    backgroundColor: 'light-dark(var(--mantine-color-sun-0), var(--mantine-color-sun-9))',
                  }}
                >
                  <Stack gap="xs">
                    <Text fw={500}>Stipend offered</Text>
                    {venue.stipendDescription ? (
                      <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                        {venue.stipendDescription}
                      </Text>
                    ) : (
                      // CFR-004: stipend description is now required at form +
                      // API level whenever stipend is offered, so this branch
                      // shouldn't fire on any new venue. Kept as defence-in-depth
                      // for legacy data only.
                      // TODO: remove once all venues backfill stipend descriptions
                      <Text size="sm">Details shared once your stay is confirmed.</Text>
                    )}
                  </Stack>
                </Card>
              )}

              <Card withBorder padding="md">
                <Stack gap="xs">
                  <Link
                    to="/request/$venueId"
                    params={{ venueId: venue.id }}
                    style={{ textDecoration: 'none' }}
                    onClick={() => {
                      track('venue.send_request_clicked', { venueId: venue.id });
                    }}
                  >
                    <Button fullWidth size="md">
                      Send a request
                    </Button>
                  </Link>
                  <Text size="sm" c="dimmed">
                    No upfront cost. This starts a conversation with{' '}
                    {venue.host.displayName.split(' ')[0]}.
                  </Text>
                </Stack>
              </Card>
            </Stack>
          </Grid.Col>
        </Grid>

        <Divider />

        {/* Availability */}
        <section aria-labelledby="availability-heading">
          <Box id="availability-heading" mb="xs">
            <SectionTitle title="Availability" order={3} />
          </Box>
          <AvailabilityCalendarSection slug={venue.slug} availability={venue.availability} hostFirstName={venue.host.displayName.split(' ')[0] ?? venue.host.displayName} />
        </section>

        <Divider />

        {/* Reviews */}
        <section aria-labelledby="reviews-heading">
          <Stack gap="md">
            <Box id="reviews-heading" mb="xs">
              <SectionTitle title="Reviews" order={3} />
            </Box>
            {venue.reviewSummary.visible && venue.reviewSummary.aggregateReviewScores ? (
              <AggregateScoresPanel scores={venue.reviewSummary.aggregateReviewScores} />
            ) : (
              <Text c="dimmed">
                Reviews appear after {venue.reviewSummary.threshold} completed exchange
                {venue.reviewSummary.threshold === 1 ? '' : 's'}.{' '}
                {venue.reviewSummary.completedExchanges > 0 && (
                  <Text component="span" c="dimmed">
                    This venue has {venue.reviewSummary.completedExchanges} so far.
                  </Text>
                )}
              </Text>
            )}
            {venue.reviews.length > 0 || venue.reviewSummary.visible ? (
              <ReviewsSection
                reviews={venue.reviews}
                reviewsTotal={venue.reviews_total}
                viewerUserId={user?.id ?? null}
                revieweeUserId={venue.reviewee_user_id}
              />
            ) : null}
          </Stack>
        </section>

        <Divider />

        {/* Map preview */}
        <section aria-labelledby="map-heading">
          <Box id="map-heading" mb="xs">
            <SectionTitle title="Where it is" order={3} />
          </Box>
          {venue.exactLocation && venue.addressLine ? (
            <Stack gap="xs">
              <Text>{venue.addressLine}</Text>
              <Text c="dimmed">
                {place}
                {venue.postcode ? ` ${venue.postcode}` : ''}
              </Text>
              <Anchor
                href={`https://www.openstreetmap.org/?mlat=${venue.lat}&mlon=${venue.lng}#map=17/${venue.lat}/${venue.lng}`}
                target="_blank"
                rel="noreferrer noopener"
                size="sm"
              >
                Open in maps
              </Anchor>
            </Stack>
          ) : (
            <Alert color="gray" variant="light">
              <Stack gap={4}>
                <Text size="sm">
                  {place}. Exact address shared once your stay is confirmed.
                </Text>
              </Stack>
            </Alert>
          )}
        </section>
      </Stack>

      {/* Lightbox */}
      <Modal
        opened={lightboxOpen}
        onClose={lightboxCtl.close}
        size="xl"
        centered
        withCloseButton
        title={venue.images[lightboxIdx]?.caption ?? venue.images[lightboxIdx]?.altText ?? venue.name}
      >
        {venue.images[lightboxIdx] && (
          <Stack gap="xs">
            <Image
              src={venue.images[lightboxIdx].url}
              alt={venue.images[lightboxIdx].altText}
              fit="contain"
              mah={600}
            />
            <Group justify="space-between">
              <Button
                variant="default"
                onClick={() =>
                  setLightboxIdx((i) => (i - 1 + venue.images.length) % venue.images.length)
                }
                disabled={venue.images.length < 2}
              >
                Previous
              </Button>
              <Text size="sm" c="dimmed">
                {lightboxIdx + 1} / {venue.images.length}
              </Text>
              <Button
                variant="default"
                onClick={() => setLightboxIdx((i) => (i + 1) % venue.images.length)}
                disabled={venue.images.length < 2}
              >
                Next
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  );
}

function NotFound({ slug }: { slug: string }): React.JSX.Element {
  return (
    <Paper p="xl" withBorder data-placeholder={`/venues/${slug}/not-found`}>
      <Stack gap="xs" align="center">
        <Title order={3}>This venue isn&apos;t live.</Title>
        <Text c="dimmed" ta="center">
          The link may be old, or the host may have paused this listing. Try browsing.
        </Text>
        <Button component={Link} to="/browse" variant="default">
          Browse venues
        </Button>
      </Stack>
    </Paper>
  );
}

function ExtrasSection({ venue }: { venue: VenueDetailDTO }): React.JSX.Element | null {
  const extras: { label: string; description: string | null }[] = [];
  if (venue.stipendOffered) {
    extras.push({ label: 'Stipend', description: venue.stipendDescription ?? null });
  }
  if (venue.travelContributionDefault) {
    extras.push({
      label: 'Travel contribution',
      description: venue.travelContributionDescription ?? null,
    });
  }
  if (venue.equipmentDefault) {
    extras.push({
      label: 'Equipment / backline',
      description: venue.equipmentDescription ?? null,
    });
  }
  if (venue.promotionDefault) {
    extras.push({ label: 'Promotion', description: venue.promotionDescription ?? null });
  }
  if (venue.recordingDefault) {
    extras.push({
      label: 'Recording or creative space',
      description: venue.recordingDescription ?? null,
    });
  }
  if (extras.length === 0) return null;
  return (
    <section aria-labelledby="extras-heading">
      <Title order={4} id="extras-heading" mb="xs">
        What this venue offers
      </Title>
      <Stack gap="sm">
        {extras.map((e) => (
          <Group key={e.label} gap={4} align="flex-start" wrap="nowrap">
            <Stack gap={2}>
              <Text fw={500} size="sm">
                {e.label}
              </Text>
              {e.description && e.description.length > 0 ? (
                <Text size="sm" c="dimmed" style={{ whiteSpace: 'pre-wrap' }}>
                  {e.description}
                </Text>
              ) : null}
            </Stack>
          </Group>
        ))}
      </Stack>
    </section>
  );
}

const AMENITY_SCOPE_ORDER = ['sound_tech', 'sleeping', 'food', 'drink', 'facilities'] as const;

function scopeLabel(scope: string): string {
  switch (scope) {
    case 'sound_tech':
      return 'Sound';
    case 'sleeping':
      return 'Sleeping';
    case 'food':
      return 'Food';
    case 'drink':
      return 'Drink';
    case 'facilities':
      return 'Facilities';
    default:
      return scope;
  }
}

function weightedAverage(scores: AggregateScores): number {
  const buckets = Object.values(scores).filter(
    (b): b is AggregateBucket => !!b && typeof b.avg === 'number',
  );
  if (buckets.length === 0) return 0;
  const totalCount = buckets.reduce((s, b) => s + b.count, 0);
  if (totalCount === 0) return 0;
  return buckets.reduce((s, b) => s + b.avg * b.count, 0) / totalCount;
}

function AggregateScoresPanel({ scores }: { scores: AggregateScores }): React.JSX.Element {
  const rows: { key: keyof AggregateScores; label: string }[] = [
    { key: 'communication', label: 'Communication' },
    { key: 'hospitality', label: 'Hospitality' },
    { key: 'performance', label: 'Performance' },
    { key: 'safety', label: 'Safety' },
    { key: 'exchangeRespected', label: 'Exchange respected' },
  ];
  return (
    <Stack gap={4}>
      {rows.map((r) => {
        const bucket = scores[r.key];
        if (!bucket) return null;
        return (
          <Group key={r.key} justify="space-between" wrap="nowrap">
            <Text size="sm">{r.label}</Text>
            <Text size="sm" fw={500}>
              ★ {bucket.avg.toFixed(1)} <Text component="span" c="dimmed">({bucket.count})</Text>
            </Text>
          </Group>
        );
      })}
    </Stack>
  );
}

type PublicAvailabilityData = {
  defaultState: 'available' | 'unavailable';
  entries: Array<{ startDate: string; endDate: string; kind: 'blocked' | 'available' }>;
  monthRange: { from: string; to: string };
};

async function fetchPublicAvailability(slug: string): Promise<PublicAvailabilityData | null> {
  try {
    const res = await fetchOk(
      `/api/venue-calendar/by-slug/${encodeURIComponent(slug)}/availability`,
    );
    return (await res.json()) as PublicAvailabilityData;
  } catch {
    return null;
  }
}

function AvailabilityCalendarSection({
  slug,
  availability,
  hostFirstName,
}: {
  slug: string;
  availability: VenueDetailDTO['availability'];
  hostFirstName: string;
}): React.JSX.Element {
  const [expanded, expandCtl] = useDisclosure(false);

  const calQuery = useQuery({
    queryKey: ['venue', slug, 'public-availability'],
    queryFn: () => fetchPublicAvailability(slug),
    staleTime: 60_000,
  });

  if (!availability) {
    return (
      <Text c="dimmed">
        {hostFirstName} hasn&apos;t shared a calendar yet. Send a request with your dates and they&apos;ll let you know.
      </Text>
    );
  }

  const calData = calQuery.data;
  const hasCalendar = calData && calData.entries.length > 0;

  return (
    <Stack gap="md">
      {hasCalendar ? (
        <>
          <Button
            variant="subtle"
            size="compact-sm"
            onClick={expandCtl.toggle}
          >
            {expanded ? 'Hide calendar' : 'Show calendar'}
          </Button>
          <Collapse expanded={expanded}>
            <AvailabilityCalendar
              entries={calData.entries}
              exchanges={[]}
              defaultState={calData.defaultState}
              readOnly
            />
          </Collapse>
        </>
      ) : null}

      <AvailabilityListFallback availability={availability} hostFirstName={hostFirstName} />
    </Stack>
  );
}

function AvailabilityListFallback({
  availability,
  hostFirstName,
}: {
  availability: NonNullable<VenueDetailDTO['availability']>;
  hostFirstName: string;
}): React.JSX.Element {
  const blocks = availability.blocks;
  const opens = availability.openWindows;
  if (blocks.length === 0 && opens.length === 0) {
    return (
      <Text c="dimmed">
        {availability.defaultState === 'available'
          ? `${hostFirstName} hasn't blocked any dates in the next 12 months. Send a request and confirm what works.`
          : `${hostFirstName} hosts on selected open dates only. Send a request and they'll respond.`}
      </Text>
    );
  }
  return (
    <Stack gap="md">
      {availability.defaultState === 'available' && blocks.length > 0 && (
        <div>
          <Text fw={500} mb={4}>
            Already booked or blocked
          </Text>
          <List size="sm" spacing={2}>
            {blocks.slice(0, 12).map((b, i) => (
              <List.Item key={i}>{formatRange(b.startDate, b.endDate)}</List.Item>
            ))}
          </List>
        </div>
      )}
      {availability.defaultState === 'unavailable' && opens.length > 0 && (
        <div>
          <Text fw={500} mb={4}>
            Open windows
          </Text>
          <List size="sm" spacing={2}>
            {opens.slice(0, 12).map((b, i) => (
              <List.Item key={i}>{formatRange(b.startDate, b.endDate)}</List.Item>
            ))}
          </List>
        </div>
      )}
    </Stack>
  );
}

function formatRange(start: string, end: string): string {
  if (start === end) return start;
  return `${start} → ${end}`;
}

// Keep `Loader` imported to satisfy bundler tree-shake (used by hooks); unused
// here but referenced if a future suspense state is introduced.
void Loader;

// ─── Wave B: open-weeks public section ─────────────────────────────────────

type PublicOpenWeek = {
  id: string;
  fromDate: number;
  toDate: number;
  notes: string | null;
};

async function fetchPublicOpenWeeks(slug: string): Promise<PublicOpenWeek[] | null> {
  try {
    const res = await fetchOk(
      `/api/open-weeks/public/venue/${encodeURIComponent(slug)}`,
      { credentials: 'include' },
    );
    const json = (await res.json()) as { items: PublicOpenWeek[] };
    return json.items;
  } catch {
    // 401 (anonymous), 403 (non-artist), 404, network — all collapse to "no
    // section". The host-side editor at /host/availability is the only writer
    // for this list; nothing else surfaces here.
    return null;
  }
}

function OpenWeeksSection({ slug }: { slug: string }): React.JSX.Element | null {
  const query = useQuery({
    queryKey: ['venue', slug, 'open-weeks'],
    queryFn: () => fetchPublicOpenWeeks(slug),
    staleTime: 60_000,
  });

  if (!query.data || query.data.length === 0) return null;

  const visible = query.data.slice(0, 4);
  const hiddenCount = query.data.length - visible.length;

  return (
    <Card withBorder padding="md" radius="md">
      <Stack gap="xs">
        <Title order={4}>Open weeks</Title>
        <Text size="sm" c="dimmed">
          Windows the host would love to fill. Get in touch if your tour fits.
        </Text>
        <Stack gap={6}>
          {visible.map((week) => (
            <Group key={week.id} gap="xs" wrap="nowrap" align="flex-start">
              <Badge variant="light" color="brand" style={{ flexShrink: 0 }}>
                {formatDateRangeMs(week.fromDate, week.toDate)}
              </Badge>
              {week.notes !== null && week.notes.length > 0 ? (
                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                  {week.notes}
                </Text>
              ) : null}
            </Group>
          ))}
        </Stack>
        {hiddenCount > 0 ? (
          <Text size="xs" c="dimmed">
            +{hiddenCount} more. Send a request to ask about other dates.
          </Text>
        ) : null}
      </Stack>
    </Card>
  );
}
